/*
  MarketDisplay.jsx
  ----------------
  Interface d'affichage d'un marché.
*/
import { useEffect, useState, useRef } from "react";
import {
  Card,
  Text,
  Group,
  Center,
  Stack,
  Divider,
  Button,
  Badge,
  Modal,
  Loader,
  Tabs,
  Textarea,
  Grid,
  Select,
  Table,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconActivityHeartbeat, IconRosetteDiscountCheck } from "@tabler/icons-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../tools/Context";
import DisplayOrder from "../tools/DisplayOrder";
import { get, post } from "../tools/Request";
import DisplayOsFile from "../tools/DisplayOsFile";
import downloadFromBackend from "../tools/DownloadFromBackend";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}/${month}/${year} `;
};

const CustomBadge = ({ color, children }) => (
  <div
    style={{
      display: "inline-block",
      padding: "8px 12px",
      backgroundColor: color,
      color: "#fff",
      borderRadius: "4px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: "1.4",
      maxWidth: "500px",
      textAlign: "left",
    }}
  >
    <Text size="sm">{children}</Text>
  </div>
);

export default function MarketDisplay() {
  const [market, setMarket] = useState(null);
  const [chats, setChats] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedOsId, setSelectedOsId] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [orderModalOpened, setOrderModalOpened] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [attestationModalOpened, setAttestationModalOpened] = useState(false);
  const [savingAttestation, setSavingAttestation] = useState(false);
  const [users, setUsers] = useState([]);
  const [marketDocuments, setMarketDocuments] = useState([]);
  const [selectedDecompte, setSelectedDecompte] = useState(null);
  const [decompteModalOpened, setDecompteModalOpened] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [selectedAccessIds, setSelectedAccessIds] = useState([]);
  const [addUserCandidateId, setAddUserCandidateId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState(null);
  const [confirmUserId, setConfirmUserId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const userCtx = useUser();
  const { token, isratp, role: currentUserRole } = userCtx;
  const queryParams = new URLSearchParams(location.search);
  const marketNumberFromUrl = queryParams.get("market") || queryParams.get("market_number") || null;
  const legacyMarketIdFromUrl = queryParams.get("id") || null;

  const chatContainerRef = useRef(null);
  const socket = useRef(null);

  const handleCreateOs = () => {
    if (!market?.id) return;
    navigate(`/CreateOS?marketId=${market.id}`);
  };
  const handleCreateDecompte = () => {
    if (!market?.id) return;
    navigate(`/newDecompte?marketId=${market.id}`);
  };

  const isUserDisplayable = (u) => {
    if (!u) return false;
    if (u.role === "administrateur") return false;
    return true;
  };

  const fetchChats = async (marketId) => {
    if (!marketId) return;
    try {
      const response = await get(`chat?market_id=${marketId}`, null, token);
      setChats(response);
    } catch (error) {
      console.error("Error fetching chat messages:", error);
    }
  };

  const fetchMarketDocuments = async (marketId) => {
    if (!marketId) return;
    try {
      const response = await get(`decompte/by-market/${marketId}`, null, token);
      setMarketDocuments(response);
    } catch (error) {
      console.error("Error fetching market documents:", error);
    }
  };

  const fetchUsersLinkedToMarketByNumber = async (marketNumber) => {
    if (!marketNumber) return;
    try {
      const response = await get(`market/market/by-number/${marketNumber}/users`, null, token);
      setUsers(response.users || []);
    } catch (error) {
      console.error("Error fetching users linked to market:", error);
      setUsers([]);
    }
  };

  useEffect(() => {
    const loadMarket = async () => {
      try {
        let marketResp;

        if (marketNumberFromUrl) {
          marketResp = await get(`market/by-number/${encodeURIComponent(marketNumberFromUrl)}`, null, token);
        } else if (legacyMarketIdFromUrl) {
          marketResp = await get(`market/${legacyMarketIdFromUrl}`, null, token);
        } else {
          setHasAccess(false);
          return;
        }

        if (marketResp?.error) {
          if (marketResp.error === "Unauthorized access to this market") setHasAccess(false);
          else setHasAccess(false);
          return;
        }

        setMarket(marketResp);

        await Promise.all([
          fetchUsersLinkedToMarketByNumber(marketResp.market_number),
          fetchMarketDocuments(marketResp.id),
          fetchChats(marketResp.id),
          (async () => {
            try {
              await post("chat/clear-notifications", { market_id: marketResp.id }, token);
            } catch (e) {
              console.error("Error clearing notifications:", e);
            }
          })(),
        ]);

        const protocol = window.location.protocol === "https:" ? "wss://" : "ws://";
        const socketUrl = `${protocol}${window.location.host}/api/`;
        socket.current = new WebSocket(socketUrl);
        socket.current.onmessage = () => fetchChats(marketResp.id);
      } catch (error) {
        console.error("Error loading market", error);
        setHasAccess(false);
      }
    };

    loadMarket();

    return () => {
      if (socket.current) socket.current.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketNumberFromUrl, legacyMarketIdFromUrl, token]);

  const openDocumentsModal = async (decompteId, decompteName) => {
    try {
      const docs = await get(`file/by-decompte/${decompteId}`, null, token);
      setSelectedDecompte({ id: decompteId, name: decompteName });
      setDocuments(docs);
      setDecompteModalOpened(true);
    } catch (err) {
      console.error("Erreur lors du chargement des documents :", err);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 100);
    return () => clearTimeout(t);
  }, [chats]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !market?.id) return;
    const messageData = { message: newMessage, type: "text", market_id: market.id };
    try {
      await post("chat", messageData, token);
      setNewMessage("");
      if (socket.current) {
        socket.current.send(JSON.stringify({ type: "new_message", market_id: market.id }));
      }
      fetchChats(market.id);
    } catch (error) {
      console.error("Error sending chat message:", error);
    }
  };

  const handleViewOS = (osId) => {
    setSelectedOsId(osId);
    setModalOpened(true);
  };

  const handleViewOrder = async (orderId) => {
    setSelectedOrderId(orderId);
    try {
      const response = await get(`order/${orderId}`, null, token);
      setSelectedOrder(response);
      setOrderModalOpened(true);
    } catch (error) {
      console.error("Error fetching order data:", error);
    }
  };

  const handleMessageClick = (message) => {
    if (!isratp) return;
    setSelectedMessage(message);
    setAttestationModalOpened(true);
  };

  const openManageAccess = async () => {
    if (!market) return;
    try {
      setAllUsersLoading(true);
      const all = await get("users/getAllUser", null, token);
      const safeAll = Array.isArray(all) ? all : [];
      setAllUsers(safeAll);

      const linkedDisplayableIds = (users || []).filter(isUserDisplayable).map((u) => String(u.id));
      setSelectedAccessIds(linkedDisplayableIds);

      setAddUserCandidateId(null);
      setManageModalOpen(true);
    } catch (e) {
      console.error("Error loading users list", e);
      showNotification({
        title: "Erreur",
        message: "Impossible de charger la liste des utilisateurs.",
        color: "red",
      });
    } finally {
      setAllUsersLoading(false);
    }
  };

  const displayableAllUsers = (allUsers || []).filter(isUserDisplayable);
  const userById = (idStr) => displayableAllUsers.find((u) => String(u.id) === idStr);

  const allUserOptions = displayableAllUsers.map((u) => ({
    value: String(u.id),
    label: `${u.email} (${u.role})`,
  }));
  const addableOptions = allUserOptions.filter((opt) => !selectedAccessIds.includes(opt.value));

  const displayableLinkedUsers = (users || []).filter(isUserDisplayable);

  const startRemoveAuthorized = (idStr) => {
    setConfirmMode("remove");
    setConfirmUserId(idStr);
    setConfirmOpen(true);
  };

  const startAddAuthorized = () => {
    if (!addUserCandidateId) return;
    setConfirmMode("add");
    setConfirmUserId(addUserCandidateId);
    setConfirmOpen(true);
  };

  // Apply server update for one user (full list swap)
  const updateServerAuthorizedForUser = async (userIdStr, nextMarkets) => {
    await post(`users/updateAuthorizedMarkets/${userIdStr}`, { authorized_markets: nextMarkets }, token);
  };

  const confirmApply = async () => {
    if (!confirmMode || !confirmUserId || !market) return;
    const marketNumber = market.market_number;
    const u = userById(confirmUserId);
    if (!u) {
      setConfirmOpen(false);
      return;
    }

    const current = Array.isArray(u.authorized_markets) ? [...u.authorized_markets] : [];

    let next;
    if (confirmMode === "add") {
      if (current.includes(marketNumber)) {
        setSelectedAccessIds((prev) => (prev.includes(confirmUserId) ? prev : [...prev, confirmUserId]));
        setAddUserCandidateId(null);
        setConfirmOpen(false);
        return;
      }
      next = [...current, marketNumber];
    } else {
      if (!current.includes(marketNumber)) {
        setSelectedAccessIds((prev) => prev.filter((id) => id !== confirmUserId));
        setConfirmOpen(false);
        return;
      }
      next = current.filter((m) => m !== marketNumber);
    }

    setConfirmLoading(true);
    try {
      await updateServerAuthorizedForUser(confirmUserId, next);

      setSelectedAccessIds((prev) => {
        if (confirmMode === "add") return prev.includes(confirmUserId) ? prev : [...prev, confirmUserId];
        return prev.filter((id) => id !== confirmUserId);
      });

      setAllUsers((prev) =>
        prev.map((user) => (String(user.id) === confirmUserId ? { ...user, authorized_markets: next } : user))
      );

      await fetchUsersLinkedToMarketByNumber(market.market_number);

      showNotification({
        title: confirmMode === "add" ? "Utilisateur ajouté" : "Utilisateur retiré",
        message: confirmMode === "add" ? "L'accès au marché a été accordé." : "L'accès au marché a été retiré.",
        color: "green",
      });
    } catch (e) {
      console.error("Error updating authorized markets", e);
      showNotification({
        title: "Erreur",
        message:
          confirmMode === "add" ? "Impossible d'ajouter cet utilisateur." : "Impossible de retirer cet utilisateur.",
        color: "red",
      });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      if (confirmMode === "add") setAddUserCandidateId(null);
      setConfirmMode(null);
      setConfirmUserId(null);
    }
  };

  if (!hasAccess) {
    return (
      <Center>
        <Text size="xl" color="red">
          Vous n'avez pas les droits requis pour accéder à cette page
        </Text>
      </Center>
    );
  }

  if (!market) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  const sortedOS =
    market.os && Array.isArray(market.os)
      ? market.os.map((item) => ({ ...item })).sort((a, b) => new Date(b.date) - new Date(a.date))
      : [];

  const sortedOrders = (market.orders || [])
    .map((item) => ({ ...item }))
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Center>
      <Stack mt="3%" w="90%">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between">
            <Group>
              <Center>
                <Group align="center" spacing="xs" mt={10}>
                  <IconActivityHeartbeat size={40} color="red" />
                  <Text weight={500} mr={-10}>
                    Vie du marché
                  </Text>
                  <Text weight={500} fw={700}>
                    {market.market_number}
                  </Text>
                  <Badge color={market.market_status === "Ouvert" ? "#00aa91" : "#e2231a"}>
                    {market.market_status}
                  </Badge>
                </Group>
              </Center>
            </Group>

            <Group>
              {currentUserRole === "chef de projet" && (
                <Button variant="light" color="#0a0082" onClick={openManageAccess}>
                  Gérer les accès
                </Button>
              )}

              {userCtx.role !== "administrateur" && userCtx.role !== "titulaire de marché" && (
                <>
                  <Button
                    variant="light"
                    color={isratp ? "#0a0082" : "#ff7e2e"}
                    onClick={handleCreateDecompte}
                    disabled={!market?.id}
                  >
                    Créer un Décompte
                  </Button>
                  <Button
                    variant="light"
                    color={isratp ? "#0a0082" : "#ff7e2e"}
                    onClick={handleCreateOs}
                    disabled={!market?.id}
                  >
                    Créer un OS
                  </Button>
                </>
              )}
            </Group>
          </Group>

          <Divider mt={10} mb={10} size="sm" />

          <Grid justify="space-between">
            <Grid.Col span={8}>
              <Stack gap="xs">
                <Text size="sm" color="dimmed">
                  {market.pilot_direction}
                </Text>
                <Text size="sm" color="dimmed">
                  {market.supplier_name}
                </Text>
                <Text size="sm" color="dimmed">
                  {market.market_description}
                </Text>
                <Text size="sm" color="dimmed">
                  Acheteur : {market.buyer_name}
                </Text>
                <Text size="sm" color="dimmed">
                  Manager : {market.market_manager}
                </Text>
                <Text size="sm" color="dimmed">
                  Label : {market.cta_label}
                </Text>
                <Group>
                  <Text size="sm" color="dimmed">
                    Date de début : {formatDate(market.validity_start)}
                  </Text>
                  <Text size="sm" color="dimmed">
                    Date de fin : {formatDate(market.validity_end)}
                  </Text>
                </Group>
              </Stack>
            </Grid.Col>

            <Divider size="sm" orientation="vertical" />

            <Grid.Col span={3}>
              <Stack align="right">
                <Text weight={500}>Utilisateurs associés à ce marché</Text>
                <Divider mb={10} size="sm" />
                <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {displayableLinkedUsers.length === 0 ? (
                    <Text color="dimmed">Aucun utilisateur lié à ce marché</Text>
                  ) : (
                    displayableLinkedUsers.map((u) => (
                      <Group mb="xs" key={u.id} position="apart">
                        <Text size="sm">{u.email}</Text>
                        <Badge color="blue">
                          {u.role === "maitre d'oeuvre" ? "MOE" : u.role === "chef de projet" ? "CdP" : u.role}
                        </Badge>
                      </Group>
                    ))
                  )}
                </div>
              </Stack>
            </Grid.Col>
          </Grid>

          <Divider size="lg" orientation="vertical" />
        </Card>

        <Group>
          <Card shadow="sm" padding="lg" radius="md" withBorder h={500} w="64.8%">
            <Group position="apart" mb="xs">
              <Text fw={700}>Messagerie {isratp && "partagée avec le fournisseur"}</Text>
            </Group>
            <Divider mb={10} size="sm" />
            <Stack
              h={userCtx.role !== "administrateur" ? "75%" : "100%"}
              spacing="xs"
              style={{ overflowY: "auto" }}
              ref={chatContainerRef}
            >
              {chats.map((chat) => (
                <Group
                  key={chat.id}
                  justify={
                    isratp && !chat.sender.isratp ? "flex-end" : !isratp && chat.sender.isratp ? "flex-end" : undefined
                  }
                  onClick={chat.type !== "os" ? () => handleMessageClick(chat) : undefined}
                  style={{ cursor: chat.type !== "os" ? "pointer" : "default" }}
                  mr={7}
                  ml={7}
                >
                  <Stack gap="xs">
                    <Group mb={-10} align="center">
                      {chat.cepaj_id && <IconRosetteDiscountCheck size={16} color="green" />}
                      <Text size="xs" c="dimmed">
                        {formatDate(chat.date)}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {chat.sender.name}
                      </Text>
                    </Group>
                    {chat.type === "os" ? (
                      <CustomBadge color="gray" component="a" href={`/myos?osName=${chat.message}`}>
                        OS {chat.message} signé
                      </CustomBadge>
                    ) : (
                      <CustomBadge color={chat.sender.isratp ? "#0a0082" : "#ff7e2e"}>{chat.message}</CustomBadge>
                    )}
                  </Stack>
                </Group>
              ))}
            </Stack>
            {userCtx.role !== "administrateur" && (
              <Group>
                <Textarea
                  w="70%"
                  placeholder="Ecrivez votre message ici"
                  value={newMessage}
                  mt="10"
                  onChange={(e) => setNewMessage(e.target.value)}
                  autosize
                  minRows={3}
                  maxRows={3}
                  styles={{ resize: "none" }}
                />
                <Stack w="27%">
                  <Button
                    mt={54}
                    variant="light"
                    color={isratp ? "#0a0082" : "#ff7e2e"}
                    onClick={handleSendMessage}
                    disabled={!market?.id}
                  >
                    Envoyer
                  </Button>
                </Stack>
              </Group>
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" h={500} withBorder w="33.8%">
            <Tabs defaultValue={userCtx.isratp ? "order" : "os"}>
              <Tabs.List grow mb={10}>
                {userCtx.isratp && <Tabs.Tab value="order">Commandes</Tabs.Tab>}
                <Tabs.Tab value="os">Ordre de service</Tabs.Tab>
                <Tabs.Tab value="decompte">Décomptes</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="os">
                <Stack mt={3}>
                  {sortedOS.length === 0 ? (
                    <Text color="dimmed">Aucun OS disponible</Text>
                  ) : (
                    sortedOS.map((os) => (
                      <Button
                        key={os.id}
                        size="compact-md"
                        variant="light"
                        color={isratp ? "#0a0082" : "#ff7e2e"}
                        onClick={() => handleViewOS(os.id)}
                      >
                        OS {os.name} - {formatDate(os.last_edit_timestamp)}
                      </Button>
                    ))
                  )}
                </Stack>
              </Tabs.Panel>

              {userCtx.isratp && (
                <Tabs.Panel value="order">
                  <Stack mt={3}>
                    {sortedOrders.map((order) => (
                      <Button
                        key={order.id}
                        size="compact-md"
                        variant="light"
                        color={isratp ? "#0a0082" : "#ff7e2e"}
                        onClick={() => handleViewOrder(order.id)}
                      >
                        Commande {order.order_number} - {formatDate(order.creation_date)}
                      </Button>
                    ))}
                  </Stack>
                </Tabs.Panel>
              )}
              <Tabs.Panel value="decompte">
                <div style={{ maxHeight: 350, overflowY: "auto", paddingRight: 4 }}>
                  <Stack mt={3}>
                    {marketDocuments.length === 0 ? (
                      <Text color="dimmed">Aucun décompte lié à ce marché</Text>
                    ) : (
                      marketDocuments.map((decompte) => (
                        <Button
                          key={decompte.id}
                          size="compact-md"
                          variant="light"
                          color={isratp ? "#0a0082" : "#ff7e2e"}
                          onClick={() => openDocumentsModal(decompte.id, decompte.name)}
                        >
                          {decompte.name}
                        </Button>
                      ))
                    )}
                  </Stack>
                </div>
              </Tabs.Panel>
            </Tabs>
          </Card>
        </Group>
      </Stack>

      {/* OS modal */}
      <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title="Détails de l'OS" size={900} centered>
        {selectedOsId && <DisplayOsFile osID={selectedOsId} />}
      </Modal>

      {/* Order modal */}
      <Modal
        opened={orderModalOpened}
        onClose={() => setOrderModalOpened(false)}
        title="Détails de la Commande"
        size="xl"
        centered
      >
        {selectedOrder ? (
          <DisplayOrder order={selectedOrder} opened={orderModalOpened} onClose={() => setOrderModalOpened(false)} />
        ) : (
          <Loader />
        )}
      </Modal>

      {/* Attestation modal */}
      <Modal
        opened={attestationModalOpened}
        onClose={() => setAttestationModalOpened(false)}
        centered
        title={selectedMessage?.cepaj_id ? "Voir la Preuve" : "Créer une Attestation"}
      >
        <Text>
          {selectedMessage?.cepaj_id
            ? "Voulez-vous voir la preuve pour ce message?"
            : "Voulez-vous créer une attestation pour ce message?"}
        </Text>
        {selectedMessage?.cepaj_id ? (
          <Button
            component="a"
            color="#0a0082"
            href={`${process.env.REACT_APP_DATALOCK_URL_WEB}proof?uid=${selectedMessage.cepaj_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Voir la preuve
          </Button>
        ) : (
          <Button
            variant="light"
            color="blue"
            onClick={async () => {
              if (!selectedMessage) return;
              setSavingAttestation(true);
              try {
                await post("chat/save-message", { message_id: selectedMessage.id }, token);
                showNotification({
                  title: "Attestation créée",
                  message: "Attestation créée avec succès",
                  color: "teal",
                  autoClose: 3000,
                });
                setAttestationModalOpened(false);
                if (market?.id) fetchChats(market.id);
              } catch (error) {
                console.error("Error creating attestation:", error);
                showNotification({
                  title: "Erreur",
                  message: "Erreur lors de la création de l'attestation",
                  color: "red",
                  autoClose: 3000,
                });
              } finally {
                setSavingAttestation(false);
              }
            }}
            mt="sm"
            loading={savingAttestation}
          >
            Créer une attestation
          </Button>
        )}
      </Modal>

      {/* Decompte documents modal */}
      <Modal
        size="lg"
        opened={decompteModalOpened}
        onClose={() => setDecompteModalOpened(false)}
        title={<Text fw={600}>Fichiers pour le décompte {selectedDecompte?.name}</Text>}
        centered
      >
        {documents.length > 0 ? (
          <Stack>
            {documents.map((doc) => (
              <Group key={doc.id} position="apart">
                <Text>{doc.name}</Text>
                <Button size="xs" onClick={() => downloadFromBackend(doc.id)}>
                  Télécharger
                </Button>
              </Group>
            ))}
          </Stack>
        ) : (
          <Text>Aucun fichier trouvé pour ce décompte.</Text>
        )}
      </Modal>

      {/* Manage Market Access modal (Chef de projet) */}
      <Modal
        opened={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
        title={`Gérer les accès au marché ${market.market_number}`}
        centered
        size="lg"
      >
        <Stack>
          <Text size="sm" c="dimmed">
            Gérez la liste des utilisateurs autorisés pour ce marché.
          </Text>

          {allUsersLoading ? (
            <Group justify="center" mt="sm">
              <Loader size="sm" />
            </Group>
          ) : (
            <>
              {/* Authorized list in a Table */}
              <Table withTableBorder withColumnBorders highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Nom</Table.Th>
                    <Table.Th>Rôle</Table.Th>
                    <Table.Th style={{ width: 120 }}>Action</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {selectedAccessIds.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={4}>
                        <Text c="dimmed">Aucun utilisateur autorisé pour l’instant.</Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    selectedAccessIds.map((idStr) => {
                      const u = userById(idStr);
                      if (!u) return null;
                      return (
                        <Table.Tr key={idStr}>
                          <Table.Td>{u.email}</Table.Td>
                          <Table.Td>
                            {u.firstname} {u.lastname}
                          </Table.Td>
                          <Table.Td>{u.role}</Table.Td>
                          <Table.Td>
                            <Group justify="flex-end">
                              <Button
                                size="xs"
                                variant="light"
                                color="#e2231a"
                                onClick={() => startRemoveAuthorized(idStr)}
                              >
                                Retirer
                              </Button>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  )}
                </Table.Tbody>
              </Table>

              <Divider my="sm" />

              {/* Add new user row */}
              <Group align="end" grow>
                <Select
                  label="Ajouter un utilisateur"
                  placeholder="Choisir un utilisateur"
                  searchable
                  data={addableOptions}
                  value={addUserCandidateId}
                  onChange={setAddUserCandidateId}
                  nothingFound="Aucun utilisateur disponible"
                />
                <Button onClick={startAddAuthorized} disabled={!addUserCandidateId}>
                  Ajouter
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>

      {/* Confirm add/remove modal */}
      <Modal
        opened={confirmOpen}
        onClose={() => {
          if (!confirmLoading) setConfirmOpen(false);
        }}
        centered
        title={confirmMode === "add" ? "Confirmer l’ajout" : "Confirmer la suppression"}
      >
        <Stack>
          <Text>
            {(() => {
              const u = confirmUserId ? userById(confirmUserId) : null;
              const who = u ? `${u.email} (${u.role})` : "cet utilisateur";
              return confirmMode === "add"
                ? `Voulez-vous autoriser ${who} à accéder au marché ${market.market_number} ?`
                : `Voulez-vous retirer l’accès de ${who} au marché ${market.market_number} ?`;
            })()}
          </Text>
          <Group justify="flex-end">
            <Button variant="light" onClick={() => !confirmLoading && setConfirmOpen(false)}>
              Annuler
            </Button>
            <Button color={confirmMode === "add" ? "green" : "#e2231a"} loading={confirmLoading} onClick={confirmApply}>
              {confirmMode === "add" ? "Confirmer l’ajout" : "Retirer l’accès"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Center>
  );
}
